import type { PrimeProcess } from '../types/primeModel';
import type { ProcessCentricProcess, ProcessStateView } from '../types/primeModelViews';
import { ArtifactStateTransitionType } from '../types/enums';
import type {
  ArtifactRole,
  ProcessTask,
  ProcessTaskWithArtifacts,
  ProcessTransition,
} from '../types/processTask';

export const mapTransitionTypeToArtifactRole = (
  transitionType: ArtifactStateTransitionType | string
): ArtifactRole => {
  switch (transitionType) {
    case ArtifactStateTransitionType.MATERIAL:
      return 'MATERIAL';
    case ArtifactStateTransitionType.RESOURCE:
      return 'RESOURCE';
    case ArtifactStateTransitionType.CREATION:
      return 'CREATION';
    case ArtifactStateTransitionType.IMPROVEMENT:
      return 'PROCESSITEM_PERSISTENT';
    case ArtifactStateTransitionType.OPERATIONAL:
    default:
      return 'PROCESSITEM';
  }
};

export const mapPrimeProcessToProcessTask = (process: PrimeProcess): ProcessTask => ({
  id: process.id,
  name: process.name,
  description: process.description,
  process_type: process.process_type,
  demand_flow_type: process.demand_flow_type,
  demand_type: process.demand_type,
  is_active: true,
  created_at: process.created_at,
  updated_at: process.updated_at ?? process.created_at,
  prime_artifact_ref: process.prime_artifact_ref,
  process_parameters: process.process_parameters,
  validation_rules: process.validation_rules,
  demand_calculation_rules: process.demand_calculation_rules,
  improvement_ref: process.improvement_ref,
  improvement_parameters: process.improvement_parameters,
});

export const mapProcessStateViewToTransition = (state: ProcessStateView): ProcessTransition => ({
  id: state.transition_id || state.id,
  name: state.transition_name || state.name,
  description: state.transition_description || state.description,
  artifact_id: state.prime_artifact_ref,
  artifact_name: state.artifact_name,
  from_state_id: state.id,
  from_state_name: state.name,
  to_state_id: state.id,
  to_state_name: state.name,
  transition_type: mapTransitionTypeToArtifactRole(state.transition_type),
  is_active: true,
});

export const collectProcessCentricTransitions = (
  view: ProcessCentricProcess
): ProcessTransition[] => {
  const seen = new Set<string>();
  const transitions: ProcessTransition[] = [];
  for (const state of [...view.input_states, ...view.output_states]) {
    const transition = mapProcessStateViewToTransition(state);
    if (seen.has(transition.id)) {
      continue;
    }
    seen.add(transition.id);
    transitions.push(transition);
  }
  return transitions;
};

export const mapProcessCentricViewToProcessTaskWithArtifacts = (
  view: ProcessCentricProcess,
  transitions: ProcessTransition[]
): Omit<ProcessTaskWithArtifacts, 'validation'> => ({
  ...mapPrimeProcessToProcessTask({
    id: view.id,
    name: view.name,
    description: view.description,
    prime_artifact_ref: view.prime_artifact_ref,
    process_type: view.process_type,
    demand_flow_type: view.demand_flow_type,
    demand_type: view.input_states[0]?.demand_type ?? view.output_states[0]?.demand_type ?? 'TARGET',
    process_parameters: view.process_parameters,
    validation_rules: view.validation_rules,
    demand_calculation_rules: {},
    created_at: new Date(0).toISOString(),
    updated_at: new Date(0).toISOString(),
  }),
  artifacts: {
    processItems: [],
    materials: [],
    resources: [],
    creations: [],
    processItemsPersistent: [],
  },
  transitions,
});
